import React from "react";
import {
  List,
  ListItem,
  ListItemText,
  Typography,
  CircularProgress,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@material-ui/core";
import { css } from "@emotion/css";
import {
  useQuestions,
  useBooks,
  useQuestionGroups,
  useAllBooks,
  useAllQuestionGroups,
  useCreateQuestion,
  useCreateBook,
  useCreateQuestionGroup,
} from "../queries";

type EntityType = "questions" | "books" | "questionGroups";

type Selection =
  | { type: "question"; id: number }
  | { type: "book"; id: number }
  | { type: "questionGroup"; id: number }
  | null;

type SidebarProps = {
  selection: Selection;
  onSelect: (selection: Selection) => void;
  onCreated: (selection: Selection) => void;
  tab: EntityType;
};

const RESPONSE_TYPES = [
  { value: "", label: "All types" },
  { value: "checkbox", label: "Checkbox" },
  { value: "select", label: "Select" },
  { value: "select-or-other", label: "Select or Other" },
  { value: "multi-select", label: "Multi-Select" },
  { value: "multi-select-grouped", label: "Multi-Select Grouped" },
  { value: "integer", label: "Integer" },
  { value: "vas", label: "VAS" },
  { value: "number-range", label: "Number Range" },
  { value: "date", label: "Date" },
  { value: "text", label: "Text" },
];

const USAGE_OPTIONS = [
  { value: "", label: "All" },
  { value: "used", label: "Used" },
  { value: "unused", label: "Unused" },
];

const Sidebar: React.VFC<SidebarProps> = ({ selection, onSelect, onCreated, tab }) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [typeFilter, setTypeFilter] = React.useState("");
  const [usageFilter, setUsageFilter] = React.useState("");

  const questionsQuery = useQuestions();
  const booksQuery = useBooks();
  const questionGroupsQuery = useQuestionGroups();
  const allBooksQuery = useAllBooks();
  const allQuestionGroupsQuery = useAllQuestionGroups();

  const createQuestion = useCreateQuestion();
  const createBook = useCreateBook();
  const createQuestionGroup = useCreateQuestionGroup();

  const handleCreateQuestion = () => {
    createQuestion.mutate(
      {
        question: { primary: "New Question" },
        responseType: { _type: "checkbox" },
      },
      {
        onSuccess: (data) => {
          onCreated({ type: "question", id: data.id });
        },
      }
    );
  };

  const handleCreateBook = () => {
    createBook.mutate(
      {
        _type: "book",
        name: "New Book",
        pages: [],
      },
      {
        onSuccess: (data) => {
          onCreated({ type: "book", id: parseInt(String(data.id), 10) });
        },
      }
    );
  };

  const handleCreateQuestionGroup = () => {
    createQuestionGroup.mutate(
      {
        _type: "question_group",
        name: "New Question Group",
        questions: [],
      },
      {
        onSuccess: (data) => {
          onCreated({ type: "questionGroup", id: parseInt(String(data.id), 10) });
        },
      }
    );
  };

  const usedQuestionIds = React.useMemo(() => {
    const ids = new Set<number>();
    allBooksQuery.data?.forEach((book) => {
      book.pages?.forEach((page) => {
        page.parts?.forEach((part) => {
          if (part._type === "question") {
            ids.add(part.questionId);
          }
        });
      });
    });
    allQuestionGroupsQuery.data?.forEach((group) => {
      group.questions?.forEach((qRef) => {
        ids.add(qRef.questionId);
      });
    });
    return ids;
  }, [allBooksQuery.data, allQuestionGroupsQuery.data]);

  const filteredQuestions = React.useMemo(() => {
    if (!questionsQuery.data) return [];

    return questionsQuery.data.filter((q) => {
      if (searchTerm && !q.question.primary.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      if (typeFilter && q.responseType._type !== typeFilter) {
        return false;
      }
      if (usageFilter) {
        const isUsed = usedQuestionIds.has(q.id);
        if (usageFilter === "used" && !isUsed) return false;
        if (usageFilter === "unused" && isUsed) return false;
      }
      return true;
    });
  }, [questionsQuery.data, searchTerm, typeFilter, usageFilter, usedQuestionIds]);

  const truncate = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const renderQuestionsList = () => {
    if (questionsQuery.isLoading) return <CircularProgress size={24} />;
    if (questionsQuery.error) return <Typography color="error">Failed to load questions</Typography>;

    return (
      <>
        <Box className={styles.filters}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            fullWidth
            onClick={handleCreateQuestion}
            disabled={createQuestion.isLoading}
          >
            {createQuestion.isLoading ? "Creating..." : "New Question"}
          </Button>

          <TextField
            size="small"
            variant="outlined"
            placeholder="Search questions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            className={styles.searchField}
          />

          <FormControl variant="outlined" size="small" fullWidth className={styles.filterField}>
            <InputLabel>Filter by type</InputLabel>
            <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as string)} label="Filter by type">
              {RESPONSE_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" fullWidth className={styles.filterField}>
            <InputLabel>Filter by usage</InputLabel>
            <Select value={usageFilter} onChange={(e) => setUsageFilter(e.target.value as string)} label="Filter by usage">
              {USAGE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="caption" color="textSecondary" className={styles.countDisplay}>
            {filteredQuestions.length} match{filteredQuestions.length !== 1 ? "es" : ""}
          </Typography>
        </Box>

        <List dense className={styles.list}>
          {filteredQuestions.map((q) => (
            <ListItem
              key={q.id}
              button
              selected={selection?.type === "question" && selection.id === q.id}
              onClick={() => onSelect({ type: "question", id: q.id })}
            >
              <ListItemText primary={truncate(q.question.primary, 40)} />
            </ListItem>
          ))}
        </List>
      </>
    );
  };

  const renderBooksList = () => {
    if (booksQuery.isLoading) return <CircularProgress size={24} />;
    if (booksQuery.error) return <Typography color="error">Failed to load books</Typography>;

    return (
      <>
        <Box className={styles.createButton}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            fullWidth
            onClick={handleCreateBook}
            disabled={createBook.isLoading}
          >
            {createBook.isLoading ? "Creating..." : "New Book"}
          </Button>
        </Box>
        <List dense className={styles.list}>
          {booksQuery.data?.map((b) => (
            <ListItem
              key={b.id}
              button
              selected={selection?.type === "book" && selection.id === b.id}
              onClick={() => onSelect({ type: "book", id: b.id })}
            >
              <ListItemText primary={b.name} />
            </ListItem>
          ))}
        </List>
      </>
    );
  };

  const renderQuestionGroupsList = () => {
    if (questionGroupsQuery.isLoading) return <CircularProgress size={24} />;
    if (questionGroupsQuery.error) return <Typography color="error">Failed to load question groups</Typography>;

    return (
      <>
        <Box className={styles.createButton}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            fullWidth
            onClick={handleCreateQuestionGroup}
            disabled={createQuestionGroup.isLoading}
          >
            {createQuestionGroup.isLoading ? "Creating..." : "New Question Group"}
          </Button>
        </Box>
        <List dense className={styles.list}>
          {questionGroupsQuery.data?.map((qg) => (
            <ListItem
              key={qg.id}
              button
              selected={selection?.type === "questionGroup" && selection.id === qg.id}
              onClick={() => onSelect({ type: "questionGroup", id: qg.id })}
            >
              <ListItemText primary={qg.name} />
            </ListItem>
          ))}
        </List>
      </>
    );
  };

  const renderList = () => {
    if (tab === "questions") return renderQuestionsList();
    if (tab === "books") return renderBooksList();
    if (tab === "questionGroups") return renderQuestionGroupsList();
    return null;
  };

  return (
    <div className={styles.container}>
      <Box className={styles.listContainer}>{renderList()}</Box>
    </div>
  );
};

export default Sidebar;

export type { Selection, EntityType };

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    height: 100%;
  `,
  listContainer: css`
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  `,
  filters: css`
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    border-bottom: 1px solid #e0e0e0;
  `,
  createButton: css`
    padding: 12px;
    border-bottom: 1px solid #e0e0e0;
  `,
  searchField: css`
    & .MuiOutlinedInput-root {
      background: white;
    }
  `,
  filterField: css`
    & .MuiOutlinedInput-root {
      background: white;
    }
  `,
  countDisplay: css`
    text-align: right;
  `,
  list: css`
    padding: 0;
    flex: 1;
    overflow-y: auto;
  `,
};
